import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { CartItem, CartState, DiscountDTO, ICartItemSingle, Language } from 'ecosystem'
import {
  arrayFindAndReplaceItem,
  arrayFindAndUpdateItem,
  getProductDiscountPrice,
  getProductPrice,
  isNullable,
  parseIntSafe
} from 'shared-utils'
import { v4 as uuidv4 } from 'uuid'
import {
  deleteCartSliceFromStorage,
  getCartSliceFromStorage,
  updateCartSliceInStorage
} from './utils'

const initialState: CartState = {
  cartLocale: null,
  cartItems: [],
  cartTotal: 0,
  cartDiscountCode: null,
  cartStateUuid: null
}

export const sumCart = (cartItems: CartItem[]) => {
  let total = 0

  cartItems.forEach((item) => {
    if (item.type === 'SINGLE') {
      const price = getProductPrice(item.meta.singleType.product)
      total += price * (parseIntSafe(item.qty) ?? 1)
    } else if (item.type === 'GROUP') {
      item.meta.groupType.items.forEach((groupItem) => {
        const price = getProductPrice(groupItem.product)
        total += price * (parseIntSafe(item.qty) ?? 1) * (groupItem.originalQty ?? 1)
      })
    }
  })

  return total
}

const isDiscountApplicableToCart = (
  cartItems: CartItem[],
  discountCode: DiscountDTO | null
): boolean => {
  return (
    !!discountCode &&
    cartItems.some((cartItem) => {
      if (cartItem.type === 'SINGLE') {
        return !!getProductDiscountPrice(cartItem.meta.singleType.product, discountCode)
      }
      return cartItem.meta.groupType.items.some(
        (groupItem) => !!getProductDiscountPrice(groupItem.product, discountCode)
      )
    })
  )
}

const slice = createSlice({
  name: 'cart',
  initialState: () => {
    return getCartSliceFromStorage() ?? initialState
  },
  reducers: {
    setCartLocale: (state, { payload: cartLocale }: PayloadAction<Language>) => {
      state.cartLocale = cartLocale
      updateCartSliceInStorage(state)
      return state
    },

    addToCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      if (cartItem.type === 'SINGLE' && !getProductPrice(cartItem.meta.singleType.product)) {
        return state
      }

      const existingCartItem = state.cartItems.find(({ id }) => id === cartItem.id)

      if (!existingCartItem) {
        state.cartItems.push(cartItem)
      } else if (cartItem.type !== existingCartItem.type) {
        arrayFindAndReplaceItem({
          arr: state.cartItems,
          newItem: cartItem,
          compare: (arrItem) => arrItem.id === cartItem.id
        })
      } else if (cartItem.type === 'SINGLE' && existingCartItem.type === 'SINGLE') {
        arrayFindAndUpdateItem({
          arr: state.cartItems,
          compare: (item) => item.id === cartItem.id,
          cb: (foundItem) => {
            const singleFoundItem = foundItem as ICartItemSingle
            const singleCartItem = cartItem

            singleFoundItem.qty = singleCartItem.qty

            if (singleCartItem.offerInfo) {
              singleFoundItem.offerInfo = singleCartItem.offerInfo
            }

            /**
             * When we add a new cart item from an offer, the price is usually lower, and we want
             * to keep this price even when we click on add to cart in Product detail page for
             * the same product
             */
            const existingPrice = singleFoundItem.meta.singleType.product.price
            const newPrice = singleCartItem.meta.singleType.product.price

            if (!isNullable(existingPrice) && !isNullable(newPrice)) {
              singleFoundItem.meta.singleType.product.price = Math.min(existingPrice, newPrice)
            } else if (!isNullable(newPrice)) {
              singleFoundItem.meta.singleType.product.price = newPrice
            }
          }
        })
      } else {
        arrayFindAndReplaceItem({
          arr: state.cartItems,
          newItem: cartItem,
          compare: (arrItem) => arrItem.id === cartItem.id
        })
      }

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
      return state
    },

    removeFromCart: (state, { payload: cartItem }: PayloadAction<CartItem>) => {
      state.cartItems = state.cartItems.filter((e) => e.id !== cartItem.id)

      if (!isDiscountApplicableToCart(state.cartItems, state.cartDiscountCode)) {
        state.cartDiscountCode = null
      }

      state.cartTotal = sumCart(state.cartItems)
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    applyDiscountCode: (state, { payload: discountCode }: PayloadAction<DiscountDTO>) => {
      if (isDiscountApplicableToCart(state.cartItems, discountCode)) {
        state.cartDiscountCode = discountCode
        state.cartStateUuid = uuidv4()

        updateCartSliceInStorage(state)
      }
    },

    removeDiscountCode: (state) => {
      // Reset all prices to original and recalculate
      state.cartDiscountCode = null
      state.cartStateUuid = uuidv4()

      updateCartSliceInStorage(state)
    },

    cleanCart: () => {
      deleteCartSliceFromStorage()
      return initialState
    }
  }
})

// available actions - add more as needed, first they will have to be declared above
export const {
  setCartLocale,
  addToCart,
  removeFromCart,
  applyDiscountCode,
  removeDiscountCode,
  cleanCart
} = slice.actions

// available selectors - add more as needed
export const selectedCart = (state: { cart: CartState }) => state.cart
export const selectedCartItems = (state: { cart: CartState }) => state.cart.cartItems
export const selectedCartDiscount = (state: { cart: CartState }) => state.cart.cartDiscountCode
export const selectedCartTotal = (state: { cart: CartState }) => state.cart.cartTotal

// Selector to get cart items with current prices (already calculated)
export const selectedCartItemsWithPrices = (state: { cart: CartState }) => state.cart.cartItems

export default slice.reducer
